import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
} from '@mui/material';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import { useTranslation } from 'react-i18next';
import CloseButton from './Common/CloseButton';

/**
 * CollectionCreateDialog Component
 * 
 * A dialog for creating a new collection. Collections are used to organize
 * and group multiple projects together for better management and navigation.
 */
const CollectionCreateDialog = ({
  open,
  onClose,
  onCreate,
  primaryColor = '#5757b0',
  hoverColor = '#7777d0',
  lightPurple = '#f0f0ff'
}) => {
  const { t } = useTranslation();
  const [collectionName, setCollectionName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const handleClose = () => {
    if (!isCreating) {
      setCollectionName('');
      onClose();
    }
  };

  const handleCreate = async () => {
    const trimmedName = collectionName.trim();
    
    if (!trimmedName) {
      return;
    }

    setIsCreating(true);
    
    try {
      await onCreate(trimmedName);
      setCollectionName('');
      onClose();
    } catch (error) {
      console.error('Error creating collection:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !isCreating) {
      event.preventDefault();
      handleCreate();
    }
  };

  const isNameEmpty = !collectionName.trim();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 12px 32px rgba(87, 87, 176, 0.2)',
          overflow: 'visible',
        }
      }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${primaryColor}, ${hoverColor})`,
          color: 'white',
          py: 2,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}
      >
        <FolderSpecialIcon sx={{ fontSize: 24 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {t('collection.create_title', 'Create New Collection')}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.85rem' }}>
            {t('collection.create_subtitle', 'Collections help organize multiple projects')}
          </Typography>
        </Box>
        <CloseButton
          onClick={handleClose}
          disabled={isCreating}
          sx={{
            color: 'white',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
          }}
        />
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ pt: 1 }}>
          <Typography
            variant="body1"
            sx={{ 
              mb: 2,
              color: 'text.secondary',
              fontSize: '0.9rem',
              lineHeight: 1.5
            }}
          >
            {t('collection.create_description', 'Enter a name for your new collection. You can organize related projects within this collection for better management and navigation.')}
          </Typography>

          <TextField
            autoFocus
            label={t('collection.name_label', 'Collection Name')}
            placeholder={t('collection.name_placeholder', 'e.g., Legal Cases, Research Projects, Client Work...')}
            fullWidth
            variant="outlined"
            value={collectionName}
            onChange={(e) => setCollectionName(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isCreating}
            error={isNameEmpty && collectionName.length > 0}
            helperText={
              isNameEmpty && collectionName.length > 0 
                ? t('collection.name_required', 'Collection name is required')
                : ''
            }
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: 'rgba(247, 247, 255, 0.5)',
                '&:hover fieldset': {
                  borderColor: hoverColor,
                },
                '&.Mui-focused fieldset': {
                  borderColor: primaryColor,
                  boxShadow: `0 0 0 2px rgba(87, 87, 176, 0.1)`,
                }
              },
              '& .MuiInputLabel-outlined.Mui-focused': {
                color: primaryColor,
              }
            }}
            InputProps={{
              sx: {
                fontSize: '1rem',
                fontWeight: 'medium'
              }
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          pt: 0,
          gap: 2,
          justifyContent: 'flex-end'
        }}
      >
        <Button
          onClick={handleClose}
          disabled={isCreating}
          sx={{
            color: 'text.secondary',
            borderRadius: '10px',
            px: 3,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          {t('common.cancel', 'Cancel')}
        </Button>
        
        <Button
          onClick={handleCreate}
          disabled={isNameEmpty || isCreating}
          variant="contained"
          startIcon={isCreating ? null : <FolderSpecialIcon />}
          sx={{
            backgroundColor: primaryColor,
            borderRadius: '10px',
            px: 4,
            py: 1,
            fontWeight: 'bold',
            boxShadow: '0 4px 12px rgba(87, 87, 176, 0.3)',
            '&:hover': {
              backgroundColor: hoverColor,
              boxShadow: '0 6px 16px rgba(87, 87, 176, 0.4)',
              transform: 'translateY(-2px)',
            },
            '&:disabled': {
              backgroundColor: 'rgba(87, 87, 176, 0.3)',
              color: 'rgba(255, 255, 255, 0.6)',
              boxShadow: 'none',
              transform: 'none',
            },
            transition: 'all 0.2s ease'
          }}
        >
          {isCreating 
            ? t('collection.creating', 'Creating...') 
            : t('collection.create_button', 'Create Collection')
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CollectionCreateDialog;
